import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useToast } from '../App';
import LoadingState from '../components/LoadingState';
import ConfirmDialog from '../components/ConfirmDialog';
import { useModalManager } from '../hooks/useModalManager';
import useBatchProcessor from '../hooks/useBatchProcessor';
import BatchProgressModal from '../components/BatchProgressModal';
import { isPro } from '../Helpers';
import PriceModal from '../components/PriceModal/PriceModal';

const { RangePicker } = DatePicker;


// Order View Modal Component
const OrderViewModal = ({ order, onClose }) => {
    if (!order) return null;

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Order Details #{order?.number || order?.id}</h3>
                    <button type="button" className="modal-close" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="modal-body">
                    <div className="order-details-grid">
                        {/* Order Information */}
                        <div className="order-section">
                            <h4>Order Information</h4>
                            <div className="detail-row">
                                <span className="label">Order Number:</span>
                                <span className="value">#{order.number || order.id}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Status:</span>
                                <span className={`chip ${order.status}`}>{order.status}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Date Created:</span>
                                <span className="value">{formatDate(order.date_created)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Total:</span>
                                <span className="value font-semibold">{formatCurrency(order.total, order.currency)}</span>
                            </div>
                        </div>

                        {/* Customer Information */}
                        <div className="order-section">
                            <h4>Customer Information</h4>
                            <div className="detail-row">
                                <span className="label">Name:</span>
                                <span className="value">{order.customer?.name || order.billing?.first_name + ' ' + order.billing?.last_name || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Email:</span>
                                <span className="value">{order.customer?.email || order.billing?.email || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Phone:</span>
                                <span className="value">{order.billing?.phone || 'N/A'}</span>
                            </div>
                        </div>

                        {/* Billing Address */}
                        {order.billing && (
                            <div className="order-section">
                                <h4>Billing Address</h4>
                                <div className="address-block">
                                    <div>{order.billing.first_name} {order.billing.last_name}</div>
                                    <div>{order.billing.address_1}</div>
                                    {order.billing.address_2 && <div>{order.billing.address_2}</div>}
                                    <div>{order.billing.city}, {order.billing.state} {order.billing.postcode}</div>
                                    <div>{order.billing.country}</div>
                                </div>
                            </div>
                        )}

                        {/* Shipping Address */}
                        {order.shipping && (
                            <div className="order-section">
                                <h4>Shipping Address</h4>
                                <div className="address-block">
                                    <div>{order.shipping.first_name} {order.shipping.last_name}</div>
                                    <div>{order.shipping.address_1}</div>
                                    {order.shipping.address_2 && <div>{order.shipping.address_2}</div>}
                                    <div>{order.shipping.city}, {order.shipping.state} {order.shipping.postcode}</div>
                                    <div>{order.shipping.country}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Order Items */}
                    {order.line_items && order.line_items.length > 0 && (
                        <div className="order-section">
                            <h4>Order Items</h4>
                            <div className="items-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Quantity</th>
                                            <th>Price</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.line_items.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.name}</td>
                                                <td>{item.sku || 'N/A'}</td>
                                                <td>{item.quantity}</td>
                                                <td>{formatCurrency(item.price, order.currency)}</td>
                                                <td>{formatCurrency(item.total, order.currency)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Order Notes */}
                    {order.customer_note && (
                        <div className="order-section">
                            <h4>Customer Notes</h4>
                            <div className="notes-block">
                                {order.customer_note}
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const Orders = () => {
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();
    const { modalConfig, closeModal, showPremiumModal } = useModalManager();
    const { batchProgress, processBatch, resetProgress, shouldUseBatchProcessing } = useBatchProcessor();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({});
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [bulkOperationLoading, setBulkOperationLoading] = useState({
        processing: false,
        completed: false,
        cancelled: false,
        delete: false
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [customRowsPerPage, setCustomRowsPerPage] = useState('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [selected, setSelected] = useState([]);
    const [globalSelected, setGlobalSelected] = useState(new Set()); // Persistent selection across pages
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewingOrder, setViewingOrder] = useState(null);

    const [activeTab, setActiveTab] = useState('orders');
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, data: null });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        dateFrom: '',
        dateTo: ''
    });
    const [dateRange, setDateRange] = useState([null, null]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    
    // Advanced filters
    const [advancedFilters, setAdvancedFilters] = useState({
        sortBy: 'date',
        sortOrder: 'desc',
        daysOld: '',
        minTotal: '',
        maxTotal: '',
        minItems: '',
        maxItems: ''
    });

    const orderStatuses = [
        { value: '', label: 'All Statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'on-hold', label: 'On Hold' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'refunded', label: 'Refunded' }
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (activeTab === 'orders') {
            loadOrders();
        }
    }, [page, rowsPerPage, filters, activeTab]);

    const loadSettings = async () => {
        try {
            const response = await api.getSettings();
            if (response.success && response.settings) {
                setSettings(response.settings); // Set the full settings object
                console.log('📋 Orders - Settings loaded:', response.settings);
            }
        } catch (err) {
            console.error('Failed to load settings:', err);
        }
    };

    const loadOrders = async () => {
        try {
            setLoading(true);
            let loadedOrders = [];

            if (rowsPerPage === 'all') {
                // For "Show All", fetch all orders by making multiple API calls
                let currentPage = 1;
                let hasMore = true;

                while (hasMore) {
                    const params = {
                        page: currentPage,
                        per_page: 100 // Use safe batch size
                    };

                    if (filters.search && filters.search.trim()) {
                        params.search = filters.search.trim();
                    }

                    if (filters.status) {
                        params.status = filters.status;
                    }

                    const response = await api.getOrders(params);

                    if (response.success) {
                        const orders = response.orders || [];
                        loadedOrders = [...loadedOrders, ...orders];
                        setTotalCount(response.total || 0);

                        // Check if we have more pages
                        hasMore = orders.length === 100 && loadedOrders.length < (response.total || 0);
                        currentPage++;
                    } else {
                        hasMore = false;
                        if (loadedOrders.length === 0) {
                            throw new Error(response.message || 'Failed to load orders');
                        }
                    }
                }

                setOrders(loadedOrders);
            } else if (rowsPerPage > 100) {
                // For large page sizes, fetch multiple pages to get the requested amount
                let startPage = Math.floor((page * rowsPerPage) / 100) + 1;
                let endPage = Math.ceil(((page + 1) * rowsPerPage) / 100);
                let totalFetched = 0;

                for (let currentPage = startPage; currentPage <= endPage && totalFetched < rowsPerPage; currentPage++) {
                    const params = {
                        page: currentPage,
                        per_page: 100
                    };

                    if (filters.search && filters.search.trim()) {
                        params.search = filters.search.trim();
                    }

                    if (filters.status) {
                        params.status = filters.status;
                    }

                    const response = await api.getOrders(params);

                    if (response.success) {
                        const orders = response.orders || [];
                        loadedOrders = [...loadedOrders, ...orders];
                        setTotalCount(response.total || 0);
                        totalFetched += orders.length;

                        if (orders.length < 100) break; // No more data
                    } else {
                        if (loadedOrders.length === 0) {
                            throw new Error(response.message || 'Failed to load orders');
                        }
                        break;
                    }
                }

                // Slice to get exactly the requested page size
                const startIndex = (page * rowsPerPage) % 100;
                const endIndex = startIndex + rowsPerPage;
                const pageOrders = loadedOrders.slice(startIndex, Math.min(endIndex, loadedOrders.length));

                setOrders(pageOrders);
                loadedOrders = pageOrders; // Update loadedOrders for selection calculation
            } else {
                // Regular pagination for 100 or fewer items
                const params = {
                    page: page + 1,
                    per_page: rowsPerPage
                };

                if (filters.search && filters.search.trim()) {
                    params.search = filters.search.trim();
                }

                if (filters.status) {
                    params.status = filters.status;
                }

                const response = await api.getOrders(params);

                if (response.success) {
                    loadedOrders = response.orders || [];
                    setOrders(loadedOrders);
                    setTotalCount(response.total || 0);
                } else {
                    throw new Error(response.message || 'Failed to load orders');
                }
            }

            // Update selected state to reflect global selection for current page
            const currentPageOrderIds = loadedOrders.map(order => order.id);
            const currentPageSelected = currentPageOrderIds.filter(id => globalSelected.has(id));
            setSelected(currentPageSelected);

        } catch (err) {
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        if (dates && dates[0] && dates[1]) {
            setFilters(prev => ({
                ...prev,
                dateFrom: dates[0].format('YYYY-MM-DD'),
                dateTo: dates[1].format('YYYY-MM-DD')
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                dateFrom: '',
                dateTo: ''
            }));
        }
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            status: '',
            dateFrom: '',
            dateTo: ''
        });
        setDateRange([null, null]);
        setAdvancedFilters({
            sortBy: 'date',
            sortOrder: 'desc',
            daysOld: '',
            minTotal: '',
            maxTotal: '',
            minItems: '',
            maxItems: ''
        });
        setPage(0);
    };

    const handleAdvancedFilterChange = (field, value) => {
        setAdvancedFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    const applyDaysOldFilter = (days) => {
        const toDate = dayjs();
        const fromDate = dayjs().subtract(days, 'day');
        setDateRange([fromDate, toDate]);
        setFilters(prev => ({
            ...prev,
            dateFrom: fromDate.format('YYYY-MM-DD'),
            dateTo: toDate.format('YYYY-MM-DD')
        }));
        setAdvancedFilters(prev => ({
            ...prev,
            daysOld: days.toString()
        }));
        setPage(0);
    };

    // Apply client-side sorting and filtering
    const getFilteredAndSortedOrders = () => {
        let filtered = [...orders];

        // Apply advanced filters
        if (advancedFilters.minTotal) {
            const minTotal = parseFloat(advancedFilters.minTotal);
            filtered = filtered.filter(order => parseFloat(order.total) >= minTotal);
        }

        if (advancedFilters.maxTotal) {
            const maxTotal = parseFloat(advancedFilters.maxTotal);
            filtered = filtered.filter(order => parseFloat(order.total) <= maxTotal);
        }

        if (advancedFilters.minItems) {
            const minItems = parseInt(advancedFilters.minItems);
            filtered = filtered.filter(order => (order.items_count || 0) >= minItems);
        }

        if (advancedFilters.maxItems) {
            const maxItems = parseInt(advancedFilters.maxItems);
            filtered = filtered.filter(order => (order.items_count || 0) <= maxItems);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let comparison = 0;

            switch (advancedFilters.sortBy) {
                case 'date':
                    comparison = new Date(a.date_created) - new Date(b.date_created);
                    break;
                case 'total':
                    comparison = parseFloat(a.total) - parseFloat(b.total);
                    break;
                case 'items':
                    comparison = (a.items_count || 0) - (b.items_count || 0);
                    break;
                case 'customer':
                    const nameA = (a.customer?.name || '').toLowerCase();
                    const nameB = (b.customer?.name || '').toLowerCase();
                    comparison = nameA.localeCompare(nameB);
                    break;
                default:
                    comparison = 0;
            }

            return advancedFilters.sortOrder === 'asc' ? comparison : -comparison;
        });

        return filtered;
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Add all current page orders to global selection
            const currentPageOrderIds = orders.map(order => order.id);
            const newGlobalSelected = new Set([...globalSelected, ...currentPageOrderIds]);
            setGlobalSelected(newGlobalSelected);
            setSelected(currentPageOrderIds);
        } else {
            // Remove all current page orders from global selection
            const currentPageOrderIds = orders.map(order => order.id);
            const newGlobalSelected = new Set(globalSelected);
            currentPageOrderIds.forEach(id => newGlobalSelected.delete(id));
            setGlobalSelected(newGlobalSelected);
            setSelected([]);
        }
    };

    const handleSelectOne = (orderId) => {
        const newGlobalSelected = new Set(globalSelected);
        let newSelected;

        if (globalSelected.has(orderId)) {
            newGlobalSelected.delete(orderId);
            newSelected = selected.filter(id => id !== orderId);
        } else {
            newGlobalSelected.add(orderId);
            newSelected = [...selected, orderId];
        }

        setGlobalSelected(newGlobalSelected);
        setSelected(newSelected);
    };

    const handleEditOrder = (order) => {
        navigate(`/orders/edit/${order.id}`);
    };

    const handleViewOrder = (order) => {
        navigate(`/orders/view/${order.id}`);
    };

    const handleExport = async () => {
        // Check if user has premium access for export CSV feature
        if (!isPro()) {
            showPremiumModal();
            return;
        }

        try {
            const response = await api.exportOrders(filters, 'csv');

            if (response.success && response.download_url) {
                window.open(response.download_url, '_blank');
                showSuccess('Export started successfully');
            }
        } catch (err) {
            showError(err.message);
        }
    };

    const handleBulkStatusUpdate = (status) => {
        const totalSelected = Array.from(globalSelected);
        if (totalSelected.length === 0) {
            showError('Please select orders to update');
            return;
        }

        const statusLabels = {
            'processing': 'Processing',
            'completed': 'Completed',
            'cancelled': 'Cancelled',
            'on-hold': 'On Hold',
            'pending': 'Pending',
            'refunded': 'Refunded'
        };

        setConfirmDialog({
            isOpen: true,
            action: 'bulkStatusUpdate',
            data: { status, statusLabel: statusLabels[status] || status, count: totalSelected.length }
        });
    };

    const executeBulkStatusUpdate = async () => {
        const { status } = confirmDialog.data;
        const totalSelected = Array.from(globalSelected);

        try {
            console.log('🔍 Starting bulk status update...', {
                orderCount: totalSelected.length,
                status: status,
                settings: settings
            });

            const shouldUseBatch = shouldUseBatchProcessing(totalSelected.length, settings);

            /* console.log('🔍 Bulk Status Update - Processing Decision:', {
                orderCount: totalSelected.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            }); */

            if (shouldUseBatch) {
                console.log('🚀 Using batch processing - showing modal');
                // Use batch processing for large datasets
                setShowBatchModal(true);

                console.log('🔄 Starting batch processing...');
                await processBatch(
                    totalSelected,
                    'updateStatus',
                    { status: status },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'orders'
                );

                console.log('✅ Batch processing completed');
                showSuccess(`Successfully updated ${totalSelected.length} orders to ${status} using batch processing`);
            } else {
                console.log('📦 Using regular processing - showing loading state');
                // Use regular processing for smaller datasets - show loading state
                const loadingKey = status === 'processing' ? 'processing' :
                    status === 'completed' ? 'completed' :
                        status === 'cancelled' ? 'cancelled' : status;
                setBulkOperationLoading(prev => ({ ...prev, [loadingKey]: true }));

                const response = await api.bulkUpdateOrderStatus(totalSelected, status);

                if (response.success) {
                    showSuccess(response.message);
                } else {
                    showError(response.message || 'Failed to update orders');
                }
            }

            setSelected([]);
            setGlobalSelected(new Set()); // Clear global selection
            await loadOrders();

        } catch (err) {
            console.error('❌ Bulk status update failed:', err);
            showError(err.message);
        } finally {
            // Reset all loading states
            setBulkOperationLoading({
                processing: false,
                completed: false,
                cancelled: false,
                delete: false
            });
            setShowBatchModal(false);
        }
    };

    const handleBulkDelete = async () => {
        const totalSelected = Array.from(globalSelected);
        if (totalSelected.length === 0) {
            showError('Please select orders to delete');
            return;
        }

        setConfirmDialog({
            isOpen: true,
            action: 'bulkDelete',
            data: { count: totalSelected.length }
        });
    };

    const executeBulkDelete = async () => {
        const totalSelected = Array.from(globalSelected);

        try {
            const shouldUseBatch = shouldUseBatchProcessing(totalSelected.length, settings);

            console.log('🔍 Bulk Delete Orders - Processing Decision:', {
                orderCount: totalSelected.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);

                await processBatch(
                    totalSelected,
                    'delete',
                    { force: true }, // Always force delete for orders
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'orders'
                );

                showSuccess(`Successfully deleted ${totalSelected.length} orders using batch processing`);
            } else {
                // Use regular processing for smaller datasets - show loading state
                setBulkOperationLoading(prev => ({ ...prev, delete: true }));

                let deleted = 0;
                for (const orderId of totalSelected) {
                    try {
                        const response = await api.deleteOrder(orderId, true);
                        if (response.success) {
                            deleted++;
                        }
                    } catch (err) {
                        console.error(`Failed to delete order ${orderId}:`, err);
                    }
                }

                if (deleted > 0) {
                    showSuccess(`Successfully deleted ${deleted} orders`);
                } else {
                    showError('Failed to delete orders');
                }
            }

            setSelected([]);
            setGlobalSelected(new Set()); // Clear global selection
            await loadOrders();

        } catch (err) {
            showError(err.message);
        } finally {
            // Reset all loading states
            setBulkOperationLoading({
                processing: false,
                completed: false,
                cancelled: false,
                delete: false
            });
            setShowBatchModal(false);
        }
    };

    const getStatusChipClass = (status) => {
        const statusClasses = {
            pending: 'chip-warning',
            processing: 'chip-primary',
            'on-hold': 'chip-warning',
            completed: 'chip-success',
            cancelled: 'chip-danger',
            refunded: 'chip-secondary'
        };
        return statusClasses[status] || 'chip-secondary';
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        const value = event.target.value;
        if (value === 'custom') {
            setShowCustomInput(true);
            return;
        }

        // Reset page to 0 when changing page size
        setPage(0);
        setRowsPerPage(value === 'all' ? 'all' : parseInt(value, 10));
        setShowCustomInput(false);
    };

    const handleCustomRowsPerPageSubmit = () => {
        const customValue = parseInt(customRowsPerPage, 10);
        if (customValue && customValue > 0 && customValue <= 10000) {
            setRowsPerPage(customValue);
            setPage(0);
            setShowCustomInput(false);
            setCustomRowsPerPage('');
        } else {
            showError('Please enter a valid number between 1 and 10,000');
        }
    };

    const handleCustomRowsPerPageCancel = () => {
        setShowCustomInput(false);
        setCustomRowsPerPage('');
    };

    // Only show full-page loader on initial load (no orders yet and no filters applied)
    const isInitialLoad = loading && orders.length === 0 && !filters.search && !filters.status;

    if (isInitialLoad) {
        return (
            <div className="page">
                <div className="container container-xl">
                    <LoadingState message="Loading orders..." />
                </div>
            </div>
        );
    }

    return (
        <div className="page orders">
            <div className="container container-xl">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h1>📦 Orders</h1>
                        <p>Manage your WooCommerce orders</p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('orders')}
                    >
                        📦 Orders List
                    </button>
                </div>

                {activeTab === 'orders' && (
                    <div className="orders-container">
                        <div className="page-header">
                            <div className="page-actions">
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => navigate('/orders/add')}
                                >
                                    +  Create Order
                                </button>
                                {settings.sync_orders && (
                                    <button
                                        className={`btn btn-secondary btn-sm ${!isPro() ? 'premium-disabled' : ''}`}
                                        onClick={handleExport}
                                        title={!isPro() ? 'Premium Feature - Upgrade to unlock' : 'Export orders to CSV'}
                                    >
                                        📥 Export CSV {!isPro() ? '🔒' : ''}
                                    </button>
                                )}

                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={loadOrders}
                                    disabled={loading}
                                >
                                    🔄 Refresh
                                </button>
                            </div>
                        </div>

                        {/* Filters Bar */}
                        <div className="filters-bar">
                            <div className="filters-row">
                                <div className="filter-item filter-search">
                                    <input
                                        type="text"
                                        placeholder="🔍 Search orders..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="search-input"
                                    />
                                </div>
                                <div className="filter-item">
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="filter-select"
                                    >
                                        {orderStatuses.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-item filter-date-range">
                                    <RangePicker
                                        value={dateRange}
                                        onChange={handleDateRangeChange}
                                        placeholder={['From Date', 'To Date']}
                                        format="YYYY-MM-DD"
                                        allowClear
                                        size="middle"
                                        className="date-range-picker"
                                        style={{ width: '280px' }}
                                    />
                                </div>
                                <div className="filter-item">
                                    <button
                                        className={`btn btn-secondary btn-sm ${showAdvancedFilters ? 'active' : ''}`}
                                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                        title="Show more filters"
                                    >
                                        {showAdvancedFilters ? '▲ Less' : '▼ More'}
                                    </button>
                                </div>
                                <div className="filter-item">
                                    <button
                                        className="btn btn-secondary btn-clear"
                                        onClick={clearFilters}
                                        title="Clear all filters"
                                    >
                                        ✕ Clear
                                    </button>
                                </div>
                            </div>

                            {/* Advanced Filters - Expandable */}
                            <div className={`advanced-filters ${showAdvancedFilters ? 'expanded' : ''} ${!isPro() ? 'premium-locked' : ''}`}>
                                {!isPro() && showAdvancedFilters && (
                                    <div className="premium-overlay" onClick={showPremiumModal}>
                                        <div className="premium-badge">
                                            Unlock Advanced Filters
                                        </div>
                                    </div>
                                )}
                                <div className="advanced-filters-content">
                                    {/* Sorting Options */}
                                    <div className="filter-section">
                                        <h4 className="filter-section-title">📊 Sort & Order</h4>
                                        <div className="filter-group">
                                            <div className="filter-item">
                                                <label>Sort By</label>
                                                <select
                                                    value={advancedFilters.sortBy}
                                                    onChange={(e) => handleAdvancedFilterChange('sortBy', e.target.value)}
                                                    className="filter-select"
                                                >
                                                    <option value="date">Order Date</option>
                                                    <option value="total">Total Price</option>
                                                    <option value="items">Item Count</option>
                                                    <option value="customer">Customer Name</option>
                                                </select>
                                            </div>
                                            <div className="filter-item">
                                                <label>Order</label>
                                                <select
                                                    value={advancedFilters.sortOrder}
                                                    onChange={(e) => handleAdvancedFilterChange('sortOrder', e.target.value)}
                                                    className="filter-select"
                                                >
                                                    <option value="asc">Ascending ↑</option>
                                                    <option value="desc">Descending ↓</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quick Date Filters */}
                                    <div className="filter-section">
                                        <h4 className="filter-section-title">📅 Quick Date Filters</h4>
                                        <div className="filter-group quick-filters">
                                            <button
                                                className={`btn btn-sm ${advancedFilters.daysOld === '7' ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => applyDaysOldFilter(7)}
                                            >
                                                Last 7 Days
                                            </button>
                                            <button
                                                className={`btn btn-sm ${advancedFilters.daysOld === '10' ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => applyDaysOldFilter(10)}
                                            >
                                                Last 10 Days
                                            </button>
                                            <button
                                                className={`btn btn-sm ${advancedFilters.daysOld === '30' ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => applyDaysOldFilter(30)}
                                            >
                                                Last 30 Days
                                            </button>
                                            <button
                                                className={`btn btn-sm ${advancedFilters.daysOld === '60' ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => applyDaysOldFilter(60)}
                                            >
                                                Last 60 Days
                                            </button>
                                            <button
                                                className={`btn btn-sm ${advancedFilters.daysOld === '90' ? 'btn-primary' : 'btn-secondary'}`}
                                                onClick={() => applyDaysOldFilter(90)}
                                            >
                                                Last 90 Days
                                            </button>
                                        </div>
                                    </div>

                                    {/* Price Range Filter */}
                                    <div className="filter-section">
                                        <h4 className="filter-section-title">💰 Price Range</h4>
                                        <div className="filter-group">
                                            <div className="filter-item">
                                                <label>Min Total</label>
                                                <input
                                                    type="number"
                                                    placeholder="0.00"
                                                    value={advancedFilters.minTotal}
                                                    onChange={(e) => handleAdvancedFilterChange('minTotal', e.target.value)}
                                                    className="filter-input"
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </div>
                                            <div className="filter-item">
                                                <label>Max Total</label>
                                                <input
                                                    type="number"
                                                    placeholder="999999.99"
                                                    value={advancedFilters.maxTotal}
                                                    onChange={(e) => handleAdvancedFilterChange('maxTotal', e.target.value)}
                                                    className="filter-input"
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Item Count Filter */}
                                    <div className="filter-section">
                                        <h4 className="filter-section-title">📦 Item Count</h4>
                                        <div className="filter-group">
                                            <div className="filter-item">
                                                <label>Min Items</label>
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    value={advancedFilters.minItems}
                                                    onChange={(e) => handleAdvancedFilterChange('minItems', e.target.value)}
                                                    className="filter-input"
                                                    min="0"
                                                    step="1"
                                                />
                                            </div>
                                            <div className="filter-item">
                                                <label>Max Items</label>
                                                <input
                                                    type="number"
                                                    placeholder="999"
                                                    value={advancedFilters.maxItems}
                                                    onChange={(e) => handleAdvancedFilterChange('maxItems', e.target.value)}
                                                    className="filter-input"
                                                    min="0"
                                                    step="1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bulk Actions */}
                        {globalSelected.size > 0 && (
                            <div className="card mb-4">
                                <div className="card-content">
                                    <div className="bulk-actions flex items-center justify-between">
                                        <div className="bulk-info">
                                            <strong>{globalSelected.size} orders selected</strong>
                                            {globalSelected.size !== selected.length && (
                                                <span className="text-gray-600 ml-2">
                                                    ({selected.length} on current page)
                                                </span>
                                            )}
                                        </div>
                                        <div className="bulk-buttons flex gap-2">
                                            <button
                                                className={`btn btn-primary btn-sm ${(!isPro() && globalSelected.size > 100) ? 'premium-disabled' : ''}`}
                                                onClick={() => {
                                                    if (!isPro() && globalSelected.size > 100) {
                                                        showPremiumModal();
                                                        return;
                                                    }
                                                    navigate('/orders/bulk-edit', { state: { selectedOrders: Array.from(globalSelected) } });
                                                }}
                                                title={(!isPro() && globalSelected.size > 100) ? 'Premium Feature - Bulk edit is limited to 100 items' : 'Bulk edit selected orders'}
                                            >
                                                ✏️ Bulk Edit {(!isPro() && globalSelected.size > 100) ? '🔒' : ''}
                                            </button>
                                            <button
                                                className={`btn btn-primary btn-sm ${bulkOperationLoading.processing ? 'btn-loading' : ''}`}
                                                onClick={() => handleBulkStatusUpdate('processing')}
                                                disabled={bulkOperationLoading.processing}
                                            >
                                                {bulkOperationLoading.processing ? (
                                                    <>
                                                        <div className="spinner"></div>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    'Mark as Processing'
                                                )}
                                            </button>
                                            <button
                                                className={`btn btn-success btn-sm ${bulkOperationLoading.completed ? 'btn-loading' : ''}`}
                                                onClick={() => handleBulkStatusUpdate('completed')}
                                                disabled={bulkOperationLoading.completed}
                                            >
                                                {bulkOperationLoading.completed ? (
                                                    <>
                                                        <div className="spinner"></div>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    'Mark as Completed'
                                                )}
                                            </button>
                                            <button
                                                className={`btn btn-danger btn-sm ${bulkOperationLoading.cancelled ? 'btn-loading' : ''}`}
                                                onClick={() => handleBulkStatusUpdate('cancelled')}
                                                disabled={bulkOperationLoading.cancelled}
                                            >
                                                {bulkOperationLoading.cancelled ? (
                                                    <>
                                                        <div className="spinner"></div>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    'Cancel Orders'
                                                )}
                                            </button>
                                            <button
                                                className={`btn btn-danger btn-sm ${bulkOperationLoading.delete ? 'btn-loading' : ''}`}
                                                onClick={handleBulkDelete}
                                                disabled={bulkOperationLoading.delete}
                                            >
                                                {bulkOperationLoading.delete ? (
                                                    <>
                                                        <div className="spinner"></div>
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    'Delete Orders'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Orders Table */}
                        <div className="card">
                            <div className="table-container">
                                <table className="orders-table">
                                    <thead>
                                        <tr>
                                            <th className="checkbox-col">
                                                <input
                                                    type="checkbox"
                                                    checked={orders.length > 0 && selected.length === orders.length}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th>Order</th>
                                            <th>Status</th>
                                            <th>Customer</th>
                                            <th>Total</th>
                                            <th>Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center">
                                                    <LoadingState message={
                                                        rowsPerPage === 'all' ? "Loading all orders..." :
                                                            rowsPerPage > 100 ? `Loading ${rowsPerPage} orders...` :
                                                                "Loading orders..."
                                                    } />
                                                </td>
                                            </tr>
                                        ) : orders.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center">
                                                    <div className="empty-state">
                                                        <div className="empty-icon">📦</div>
                                                        <p>No orders found</p>
                                                        <p className="text-gray-600">Try adjusting your filters or sync data</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            getFilteredAndSortedOrders().map((order) => (
                                                <tr key={order.id} className="order-row">
                                                    <td className="checkbox-col">
                                                        <input
                                                            type="checkbox"
                                                            checked={globalSelected.has(order.id)}
                                                            onChange={() => handleSelectOne(order.id)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="order-info">
                                                            <div className="order-number">
                                                                #{order.number || order.id}
                                                            </div>
                                                            <div className="order-items text-gray-600">
                                                                {order.items_count} items
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={`chip ${getStatusChipClass(order.status)}`}>
                                                            {order.status}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="customer-info">
                                                            <div className="customer-name">
                                                                {order.customer?.name || 'N/A'}
                                                            </div>
                                                            <div className="customer-email text-gray-600">
                                                                {order.customer?.email || 'N/A'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="order-total font-semibold">
                                                            {formatCurrency(order.total, order.currency || 'USD')}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="order-date">
                                                            {formatDate(order.date_created)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="order-actions flex gap-1">
                                                            <button
                                                                className="btn btn-secondary btn-sm"
                                                                onClick={() => handleEditOrder(order)}
                                                                title="Edit Order"
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                className="btn btn-secondary btn-sm"
                                                                onClick={() => handleViewOrder(order)}
                                                                title="View Order"
                                                            >
                                                                👁️
                                                            </button>

                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalCount > 0 && (
                                <div className="table-pagination">
                                    <div className="pagination-info">
                                        {rowsPerPage === 'all' ? (
                                            `Showing all ${totalCount} orders`
                                        ) : (
                                            `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, totalCount)} of ${totalCount} orders`
                                        )}
                                        {globalSelected.size > 0 && (
                                            <span className="selected-info"> • {globalSelected.size} selected</span>
                                        )}
                                    </div>
                                    <div className="pagination-controls flex items-center gap-2">
                                        <select
                                            value={showCustomInput ? 'custom' : rowsPerPage}
                                            onChange={handleRowsPerPageChange}
                                            className="rows-per-page"
                                        >
                                            <option value={10}>10 per page</option>
                                            <option value={25}>25 per page</option>
                                            <option value={50}>50 per page</option>
                                            <option value={100}>100 per page</option>
                                            <option value={200}>200 per page</option>
                                            <option value={500}>500 per page</option>
                                            <option value={1000}>1000 per page</option>
                                            <option value="all">Show All</option>
                                            <option value="custom">Custom...</option>
                                        </select>

                                        {showCustomInput && (
                                            <div className="custom-rows-input">
                                                <input
                                                    type="number"
                                                    value={customRowsPerPage}
                                                    onChange={(e) => setCustomRowsPerPage(e.target.value)}
                                                    placeholder="Enter number (1-10000)"
                                                    min="1"
                                                    max="10000"
                                                    className="custom-input"
                                                />
                                                <button
                                                    onClick={handleCustomRowsPerPageSubmit}
                                                    className="btn btn-primary btn-xs"
                                                >
                                                    Apply
                                                </button>
                                                <button
                                                    onClick={handleCustomRowsPerPageCancel}
                                                    className="btn btn-secondary btn-xs"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}

                                        {rowsPerPage !== 'all' && (
                                            <>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => handlePageChange(page - 1)}
                                                    disabled={page === 0}
                                                >
                                                    ← Previous
                                                </button>
                                                <span className="page-info">
                                                    Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
                                                </span>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => handlePageChange(page + 1)}
                                                    disabled={(page + 1) * rowsPerPage >= totalCount}
                                                >
                                                    Next →
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                )}

                {/* View Order Modal */}
                {viewDialogOpen && (
                    <OrderViewModal
                        order={viewingOrder}
                        onClose={() => {
                            setViewDialogOpen(false);
                            setViewingOrder(null);
                        }}
                    />
                )}

                {/* Confirmation Dialog */}
                <ConfirmDialog
                    isOpen={confirmDialog.isOpen}
                    onClose={() => setConfirmDialog({ isOpen: false, action: null, data: null })}
                    onConfirm={() => {
                        if (confirmDialog.action === 'bulkDelete') {
                            executeBulkDelete();
                        } else if (confirmDialog.action === 'bulkStatusUpdate') {
                            executeBulkStatusUpdate();
                        }
                        setConfirmDialog({ isOpen: false, action: null, data: null });
                    }}
                    title={confirmDialog.action === 'bulkStatusUpdate' ? 'Confirm Status Update' : 'Confirm Delete'}
                    confirmText={confirmDialog.action === 'bulkStatusUpdate' ? 'Update Status' : 'Delete Orders'}
                    confirmButtonClass={confirmDialog.action === 'bulkDelete' ? 'btn-danger' : 'btn-primary'}
                >
                    {confirmDialog.action === 'bulkDelete' && (
                        <>
                            <p>Are you sure you want to delete <strong>{confirmDialog.data?.count} orders</strong>?</p>
                            <p className="text-danger">This action cannot be undone.</p>
                        </>
                    )}
                    {confirmDialog.action === 'bulkStatusUpdate' && (
                        <p>Are you sure you want to mark <strong>{confirmDialog.data?.count} orders</strong> as <strong>{confirmDialog.data?.statusLabel}</strong>?</p>
                    )}
                </ConfirmDialog>

                {/* Premium Modal */}
                <PriceModal
                    isOpen={modalConfig.isOpen}
                    onClose={closeModal}
                    onConfirm={modalConfig.onConfirm}
                    onDecline={closeModal}
                    type={modalConfig.type}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    confirmText={modalConfig.confirmText}
                    declineText={modalConfig.declineText}
                    position={modalConfig.position}
                />

                {/* Batch Progress Modal */}
                <BatchProgressModal
                    isOpen={showBatchModal}
                    onClose={() => {
                        setShowBatchModal(false);
                        resetProgress();
                    }}
                    progress={batchProgress}
                    currentBatch={batchProgress.currentBatch}
                    totalBatches={batchProgress.totalBatches}
                    processedItems={batchProgress.processedItems}
                    totalItems={batchProgress.totalItems}
                    operationType="orders"
                    isCompleted={batchProgress.isCompleted}
                    error={batchProgress.error}
                />

            </div>
        </div>
    );
};


export default Orders;